import { IPwaPreviewConfig } from '@adminPortal/_common/_interfaces/IPwaPreviewConfig';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoggerEventsEnum } from '@pwaPortal/core/enums/logger-events.enum';
import { AppService } from '@pwaPortal/core/services/app/app.service';
import { LoggerService } from '@pwaPortal/core/services/logger/logger.service';
import { PwaProcessService } from '@pwaPortal/core/services/pwa-process/pwa-process.service';
import { ClassicTemplateComponent } from '@sharedLib/components/pwa-templates/classic-template/classic-template.component';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { HelperService } from '@pwaPortal/core/services/helper/helper.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject, take } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ModernTemplateComponent } from '@sharedLib/components/pwa-templates/modern-template/modern-template.component';

@Component({
  selector: 'app-lp',
  templateUrl: './lp.component.html',
  styleUrls: ['./lp.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [ClassicTemplateComponent, ModernTemplateComponent, AsyncPipe],
})
export class LpComponent implements OnInit, AfterViewInit, OnDestroy {
  private document = inject(DOCUMENT);
  @Input() lpConfig!: IPwaPreviewConfig;

  public clickCount = 0;
  public isInitialization = false;
  public isAppInstalled = false;
  public installPrompt = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  private shouldInstall$: Observable<boolean>;
  private startInstall$: Observable<boolean>;
  private appInstalled$: Observable<boolean>;
  public installResult$: Observable<string>;

  public contentLanguageCode = 'en';

  @Output() installPWA: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private cdr: ChangeDetectorRef,
    private deviceService: DeviceDetectorService,
    private appService: AppService,
    private pwaService: PwaProcessService,
    private logger: LoggerService,
    private route: ActivatedRoute,
    private helperService: HelperService
  ) {
    this.shouldInstall$ = this.pwaService.shouldInstall$;
    this.startInstall$ = this.pwaService.startInstall$;
    this.appInstalled$ = this.pwaService.appInstalled$;
    this.installResult$ = this.pwaService.installResult$;
  }

  async ngOnInit(): Promise<void> {
    const installedApps = await (navigator as any)?.getInstalledRelatedApps();
    if (installedApps.length > 0) {
      this.isAppInstalled = true;
    }
  }

  async ngAfterViewInit(): Promise<void> {
    const browserLanguage = window?.navigator?.language?.split('-')?.shift();
    const defaultLanguage = this.lpConfig.content.filter((el) => el.isDefault)[0].languageCode;
    const languages = this.lpConfig.content.map((el) => el.languageCode as string);

    if (browserLanguage && languages.includes(browserLanguage)) {
      this.contentLanguageCode = browserLanguage;
    } else {
      this.contentLanguageCode = defaultLanguage;
    }

    const lang = this.route.snapshot.queryParams['lang'];

    if (lang) {
      this.contentLanguageCode = lang;
    }

    const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi', 'ji', 'iw', 'ku', 'ml'];
    const dir = rtlLanguages.includes(this.contentLanguageCode) ? 'rtl' : 'ltr';

    this.document.documentElement.lang = this.contentLanguageCode;
    this.document.documentElement.dir = dir;

    this.shouldInstall$.pipe(takeUntil(this.destroy$)).subscribe((condition) => {
      this.installPrompt = condition;
      this.cdr.detectChanges();
    });

    this.appInstalled$
      .pipe(
        filter((val) => val),
        takeUntil(this.destroy$)
      )
      .subscribe((condition: boolean) => {
        this.isAppInstalled = condition;
        this.cdr.detectChanges();
      });

    this.startInstall$
      .pipe(
        filter((val) => val),
        takeUntil(this.destroy$)
      )
      .subscribe((condition: boolean) => {
        this.isInitialization = condition;
        this.cdr.detectChanges();
      });
  }

  async reload() {
    if (this.installPrompt || this.isInitialization) return;
    window.location.reload();
  }

  async onInstallBtn() {
    this.logger.sendPxEvent();
    if (this.installPrompt) {
      this.install();
      return;
    }

    this.clickCount += 1;
    if (this.clickCount === 3) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } else if (this.clickCount === 5) {
      this.reload();
    }
  }

  async onOpenBtn() {
    const pwaId = this.helperService.getPwaId();
    const hostname = window.location.hostname;

    const hostUrl = `https://${hostname}/pwa/${pwaId}/pwa.html`;
    if (this.deviceService.browser.toLowerCase() !== 'chrome') {
      const chromeIntent = `intent://${hostUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = chromeIntent;
    } else {
      window.open(hostUrl, '_blank');
    }
  }

  install() {
    if (this.isInitialization) return;
    this.logger.sendFetchEvent(LoggerEventsEnum.INSTALL);
    this.pwaService.installApp();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
